import { useEffect, useCallback } from 'react';
import { Platform } from 'react-native';

export const SHORTCUTS = {
  NEW_WORK: {
    keys: ['⌘', '⌥', 'N'], action: 'shortcuts.newWork', group: 'work', allowInInput: true,
    combo: e => (e.metaKey || e.ctrlKey) && e.altKey && !e.shiftKey && e.code === 'KeyN',
  },
  WORK_SEND: {
    keys: ['⌘', 'Enter'], action: 'shortcuts.workSend', group: 'work', allowInInput: true, capture: true,
    combo: e => (e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && e.key === 'Enter',
  },
  WORK_SEND_NOW: {
    keys: ['⌘', '⇧', 'Enter'], action: 'shortcuts.workSendNow', group: 'work', allowInInput: true, capture: true,
    combo: e => (e.metaKey || e.ctrlKey) && !e.altKey && e.shiftKey && e.key === 'Enter',
  },
  TOGGLE_SIDEBAR: {
    keys: ['⌘', '⇧', 'S'],
    action: 'shortcuts.toggleSidebar',
    combo: (e) => (e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 's'
  },
  NEW_CHAT: {
    keys: ['⌘', '⇧', 'O'],
    action: 'shortcuts.newChat',
    combo: (e) => (e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'o'
  },
  NEW_TEMP_CHAT: {
    keys: ['⌘', '⇧', 'I'],
    action: 'shortcuts.newTemporaryChat',
    combo: (e) => (e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'i'
  },
  FOCUS_CHAT_INPUT: {
    keys: ['⌘', 'Esc'],
    action: 'shortcuts.focusChatInput',
    combo: (e) => (e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey && e.key === 'Escape'
  },
  FOCUS_SEARCH: {
    keys: ['⌘', 'K'],
    action: 'shortcuts.focusSearch',
    combo: (e) => (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k'
  },
  OPEN_FILE_UPLOAD: {
    keys: ['⌘', 'U'],
    action: 'shortcuts.openFileUpload',
    combo: (e) => (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'u'
  },
  OPEN_SHORTCUTS: {
    keys: ['⌘', '/'],
    action: 'shortcuts.openShortcuts',
    combo: (e) => (e.metaKey || e.ctrlKey) && e.key === '/'
  },
  GO_TO_HOME: {
    keys: ['g', 'h'],
    macKeys: ['⌘', '⇧', '1'],
    action: 'shortcuts.goToHome',
    combo: (e, pressedKeys) => e.key.toLowerCase() === 'h' && pressedKeys.has('g')
  },
  GO_TO_AGENTS: {
    keys: ['g', 'a'],
    macKeys: ['⌘', '⇧', '2'],
    action: 'shortcuts.goToAgents',
    combo: (e, pressedKeys) => e.key.toLowerCase() === 'a' && pressedKeys.has('g')
  },
  GO_TO_MODELS: {
    keys: ['g', 'm'],
    macKeys: ['⌘', '⇧', '3'],
    action: 'shortcuts.goToModels',
    combo: (e, pressedKeys) => e.key.toLowerCase() === 'm' && pressedKeys.has('g')
  },
  GO_TO_FILES: {
    keys: ['g', 'f'],
    macKeys: ['⌘', '⇧', '4'],
    action: 'shortcuts.goToFiles',
    combo: (e, pressedKeys) => e.key.toLowerCase() === 'f' && pressedKeys.has('g')
  },
  GO_TO_SETTINGS: {
    keys: ['g', 's'],
    macKeys: ['⌘', ','],
    action: 'shortcuts.goToSettings',
    combo: (e, pressedKeys) => e.key.toLowerCase() === 's' && pressedKeys.has('g')
  },
};

export const shortcutHandlers = new Map();

// A shortcut can have several mounted consumers (layout, sidebar, composer).
// Install one listener, so one key press never dispatches an action repeatedly.
let subscribers = 0;
let removeListeners;
export function dispatchShortcut(key, event) {
  const handlers = shortcutHandlers.get(key);
  if (!handlers) return false;
  let handled = false;
  for (const handler of [...handlers].reverse()) {
    if (handler(event) !== false) {
      handled = true;
      if (SHORTCUTS[key]?.group === 'work') break;
    }
  }
  return handled;
}

function subscribeToKeyboardEvents() {
  if (subscribers++ === 0) {
    const pressedKeys = new Set();
    const handleKeyDown = (event, capture) => {
      if (event.defaultPrevented || event.repeat || !event.key || event.isComposing || event.keyCode === 229 || event.getModifierState?.('AltGraph')) return;
      const target = event.target;
      const isEditing = target?.closest?.('input, textarea, select, [contenteditable]:not([contenteditable="false"]), [role="textbox"]');
      if (!capture && !isEditing) pressedKeys.add(event.key.toLowerCase());
      for (const [key, shortcut] of Object.entries(SHORTCUTS)) {
        if (!!shortcut.capture !== capture || (isEditing && !shortcut.allowInInput)) continue;
        if (shortcut.combo(event, pressedKeys) && dispatchShortcut(key, event)) {
          event.preventDefault();
          if (capture) event.stopPropagation();
          break;
        }
      }
    };
    const captureKey = event => handleKeyDown(event, true);
    const bubbleKey = event => handleKeyDown(event, false);
    const keyUp = event => pressedKeys.delete(event.key?.toLowerCase());
    const blur = () => pressedKeys.clear();
    window.addEventListener('keydown', captureKey, true);
    window.addEventListener('keydown', bubbleKey);
    window.addEventListener('keyup', keyUp);
    window.addEventListener('blur', blur);
    removeListeners = () => {
      window.removeEventListener('keydown', captureKey, true);
      window.removeEventListener('keydown', bubbleKey);
      window.removeEventListener('keyup', keyUp);
      window.removeEventListener('blur', blur);
    };
  }
  return () => { if (--subscribers === 0) { removeListeners(); removeListeners = null; } };
}

export default function useKeyboardShortcuts(customHandlers = {}) {
  // Register custom handlers
  useEffect(() => {
    // Merge custom handlers with existing ones
    Object.entries(customHandlers).forEach(([key, handler]) => {
      if (!shortcutHandlers.has(key)) {
        shortcutHandlers.set(key, new Set());
      }
      shortcutHandlers.get(key).add(handler);
    });

    // Cleanup
    return () => {
      Object.entries(customHandlers).forEach(([key, handler]) => {
        const handlers = shortcutHandlers.get(key);
        if (handlers) {
          handlers.delete(handler);
          if (handlers.size === 0) {
            shortcutHandlers.delete(key);
          }
        }
      });
    };
  }, [customHandlers]);

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return undefined;
    return subscribeToKeyboardEvents();
  }, []);

  return {
    SHORTCUTS,
    registerHandler: useCallback((shortcutKey, handler) => {
      if (!shortcutHandlers.has(shortcutKey)) {
        shortcutHandlers.set(shortcutKey, new Set());
      }
      shortcutHandlers.get(shortcutKey).add(handler);
      return () => {
        const handlers = shortcutHandlers.get(shortcutKey);
        if (handlers) {
          handlers.delete(handler);
          if (handlers.size === 0) {
            shortcutHandlers.delete(shortcutKey);
          }
        }
      };
    }, []),
  };
}
